/**
 * Handles display of search form dialog.
 */
export default class SearchExpander {
  /**
   * Constructor.
   */
  constructor() {
    this.behaviorType = {
      Show: 1,
      Expand: 2,
      Focus: 4,
      SubmitOnEnter: 8,
      ToggleClass: 16,
      Modal: 32,
    };

    this.classNames = {
      modalActive: 'js-modal-overlay-active',
    };

    this.errorType = {
      ERR_BUTTON_REQUIRED:
        'A button selector is required during initialization.',
      ERR_SUBMIT_BUTTON_REQUIRED:
        'A submit button selector is required during initialization',
      ERR_SEARCH_INPUT_REQUIRED:
        'a selector for the search input is required during initialization.',
      ERR_OPTION_REQUIRED:
        'Behavior Type(s) must be specified during initialization.',
      ERR_URL_REQUIRED:
        'A URL must be provided if SubmitOnEnter is included in the options.',
      ERR_ALREADY_INITIALIZED:
        'The object is already initialized. Did you mean to call clear()?',
    };

    this.listeners = {
      buttonClickListener: undefined,
      submitButtonClickListener: undefined,
      inputKeyDownListener: undefined,
      inputBlurListener: undefined,
      modalCloseListener: undefined,
    };

    this.configuration = {
      behavior: 0,
      buttonSelector: '',
      submitButtonSelector: '',
      searchSelector: '',
      targetUrl: '',
      contractedWidth: '0',
      expandedWidth: '100%',
      contractedClass: '',
      expandedClass: '',
      modalClass: '',
      modalCancel: '',
    };

    this.isInitialized = false;
  }

  /**
   * Returns the configuration.
   */
  get Configuration() {
    return this.configuration;
  }

  /**
   * Sets the configuration.
   * @param {Object} value The configuration object.
   */
  set Configuration(value) {
    if (this.isInitialized) {
      throw this.errorType.ERR_ALREADY_INITIALIZED;
    }

    this.configuration = value;
  }

  /**
   * Returns the behavior type.
   */
  get Option() {
    return this.behaviorType;
  }

  /**
   * Initializes the class instance.
   * @param {any} options              Designates the options to be used (from
   *                                   BehaviorType).
   * @param {any} buttonSelector       A CSS selector that identifies the
   *                                   button.
   * @param {any} searchSelector       A CSS selector that identifies the input
   *                                   field that will receive search text.
   * @param {any} submitButtonSelector A CSS selector that identifies the search
   *                                   submit button.
   * @param {any} targetUrl            The target url where search data is
   *                                   submitted.
   * @param {any} contractedWidth      The width applied in the contracted
   *                                   state.
   * @param {any} expandedWidth        The width applied in the expanded state.
   * @param {any} contractedClass      The class applied in the contracted
   *                                   state.
   * @param {any} expandedClass        The class applied in the expanded state.
   * @param {any} modalClass           The class of a div that is displayed as
   *                                   modal.
   * @param {any} modalCancel          The class of an element that will close
   *                                   the modal display.
   */
  init(
    options,
    buttonSelector,
    searchSelector,
    submitButtonSelector,
    targetUrl,
    contractedWidth,
    expandedWidth,
    contractedClass,
    expandedClass,
    modalClass,
  ) {
    // Do some checks up front to make sure everything is in order.
    if (this.isInitialized) {
      throw this.errorType.ERR_ALREADY_INITIALIZED;
    }

    if (options === undefined) {
      throw this.errorType.ERR_OPTION_REQUIRED;
    } else {
      this.configuration.behavior = options;
    }

    if (
      buttonSelector === undefined &&
      this.configuration.buttonSelector === ''
    ) {
      throw this.errorType.ERR_BUTTON_REQUIRED;
    } else {
      if (this.configuration.buttonSelector === '') {
        this.configuration.buttonSelector = buttonSelector;
      }
    }

    if (
      searchSelector === undefined &&
      this.configuration.searchSelector === ''
    ) {
      throw this.errorType.ERR_SEARCH_INPUT_REQUIRED;
    } else {
      if (this.configuration.searchSelector === '') {
        this.configuration.searchSelector = searchSelector;
      }
    }

    if (
      submitButtonSelector === undefined &&
      this.configuration.submitButtonSelector === ''
    ) {
      throw this.errorType.ERR_SUBMIT_BUTTON_REQUIRED;
    } else {
      if (this.configuration.submitButtonSelector === '') {
        this.configuration.submitButtonSelector = submitButtonSelector;
      }
    }

    if (
      (
        (this.configuration.behavior & this.behaviorType.SubmitOnEnter) ===
          this.behaviorType.SubmitOnEnter
      ) && (targetUrl === undefined && this.configuration.targetUrl === '')
    ) {
      throw this.errorType.ERR_URL_REQUIRED;
    } else {
      if (targetUrl !== undefined) {
        this.configuration.targetUrl = targetUrl;
      }
    }

    if (contractedWidth !== undefined) {
      this.configuration.contractedWidth = contractedWidth;
    }

    if (expandedWidth !== undefined) {
      this.configuration.expandedWidth = expandedWidth;
    }

    if (contractedClass !== undefined) {
      this.configuration.contractedClass = contractedClass;
    }

    if (expandedClass !== undefined) {
      this.configuration.expandedClass = expandedClass;
    }

    if (modalClass !== undefined) {
      this.configuration.modalClass = modalClass;

      if (window.modalCancel !== undefined) {
        this.configuration.modalCancel = window.modalCancel;
      }
    }

    // So far so good. Now apply the configuration.
    this.isInitialized = this.applyConfiguration();
  }

  /**
   * Sets up handlers based on configuration settings.
   * @return {boolean} Whether the configuration was applied.
   */
  applyConfiguration() {
    try {
      const searchButton = document.querySelector(
        this.configuration.buttonSelector,
      );
      const searchInput = document.querySelector(
        this.configuration.searchSelector,
      );
      const submitButton = document.querySelector(
        this.configuration.submitButtonSelector,
      );

      if (!searchButton || !searchInput || !submitButton) {
        return false;
      }

      searchButton.addEventListener(
        'click',
        this.listeners.buttonClickListener = this.buttonClickHandler.bind(this),
      );

      submitButton.addEventListener(
        'click',
        this.listeners.submitButtonClickListener = this.submitButtonClickHandler
          .bind(this),
      );

      searchInput.addEventListener(
        'keydown',
        this.listeners.inputKeyDownListener = this.inputKeyDownHandler
          .bind(this),
      );

      searchInput.addEventListener(
        'blur',
        this.listeners.inputBlurListener = this.inputBlurHandler.bind(this),
      );

      if (
        this.configuration.modalCancel !== '' &&
        this.configuration.modalCancel !== undefined
      ) {
        const modalCancel = document.querySelector(
          this.configuration.modalCancel,
        );

        modalCancel.addEventListener(
          'click',
          this.listeners.modalCloseListener = this.modalCloseHandler.bind(this),
        );
      }
    } catch (e) {
      return false;
    }

    return true;
  }

  /**
   * Resets the class instance to its pre-initialized state.
   */
  clear() {
    const searchButton = document.querySelector(
      this.configuration.buttonSelector,
    );

    const searchInput = document.querySelector(
      this.configuration.searchSelector,
    );

    const submitButton = document.querySelector(
      this.configuration.submitButtonSelector,
    );

    // Remove the listeners.
    searchButton.removeEventListener(
      'click',
      this.listeners.buttonClickListener,
    );

    submitButton.removeEventListener(
      'click',
      this.listeners.submitButtonClickListener,
    );

    searchInput.removeEventListener(
      'keydown',
      this.listeners.inputKeyDownListener,
    );

    searchInput.removeEventListener('blur', this.listeners.inputBlurListener);

    // Restore default values.
    this.configuration = {
      behavior: 0,
      buttonSelector: '',
      submitButtonSelector: '',
      searchSelector: '',
      targetUrl: '',
      contractedWidth: '0',
      expandedWidth: '100%',
      contractedClass: '',
      expandedClass: '',
    };

    this.listeners = {
      buttonClickListener: undefined,
      submitButtonClickListener: undefined,
      inputKeyDownListener: undefined,
      inputBlurListener: undefined,
    };

    // Set flag back.
    this.isInitialized = false;
  }

  /**
   * Sets handlers for button clicks.
   */
  buttonClickHandler() {
    if (
      (this.configuration.behavior & this.behaviorType.Show) ===
      this.behaviorType.Show
    ) {
      this.show();
    }

    if (
      (this.configuration.behavior & this.behaviorType.Expand) ===
      this.behaviorType.Expand
    ) {
      this.expand();
    }

    if (
      (this.configuration.behavior & this.behaviorType.ToggleClass) ===
      this.behaviorType.ToggleClass
    ) {
      this.toggleOn();
    }

    if (
      (this.configuration.behavior & this.behaviorType.Modal) ===
      this.behaviorType.Modal
    ) {
      this.showModal();
    }

    if (
      (this.configuration.behavior & this.behaviorType.Focus) ===
      this.behaviorType.Focus
    ) {
      this.focus();
    }
  }

  /**
   * Handles clicks on the search submit button.
   * @param {*} e The click event.
   */
  submitButtonClickHandler(e) {
    this.submit(e);
  }

  /**
   * Hides the modal dialog box.
   */
  modalCloseHandler() {
    this.hideModal();
  }

  /**
   * Handles keyDown events on the search text box.
   * @param {*} e The keyDown event.
   */
  inputKeyDownHandler(e) {
    if (
      (this.configuration.behavior & this.behaviorType.SubmitOnEnter) ===
        this.behaviorType.SubmitOnEnter
    ) {
      this.submit(e);
    }

    if (e.key === 'Escape') {
      this.hideModal();
    }
  }

  /**
   * Handles blur events on the search text box.
   */
  inputBlurHandler() {
    if (
      (
        (this.configuration.behavior & this.behaviorType.Expand) ===
          this.behaviorType.Expand
      ) && (
        (this.configuration.behavior & this.behaviorType.Focus) ===
          this.behaviorType.Focus
      )
    ) {
      this.contract();
    }

    if (
      (
        (this.configuration.behavior & this.behaviorType.Show) ===
          this.behaviorType.Show
      ) && (
        (this.configuration.behavior & this.behaviorType.Focus) ===
          this.behaviorType.Focus
      )
    ) {
      this.hide();
    }
    if (
      (this.configuration.behavior & this.behaviorType.ToggleClass) ===
        this.behaviorType.ToggleClass
    ) {
      this.toggleOff();
    }
  }

  /**
   * Shows the dialog box.
   */
  show() {
    const searchInput = document.querySelector(
      this.configuration.searchSelector,
    );

    searchInput.style.visibility = 'visible';
  }

  /**
   * Hides the dialog box.
   */
  Hide() {
    const searchInput = document.querySelector(
      this.configuration.searchSelector,
    );

    searchInput.style.visibility = 'hidden';
  }

  /**
   * Expands the search text box.
   */
  expand() {
    const searchInput = document.querySelector(
      this.configuration.searchSelector,
    );

    searchInput.style.width = this.configuration.expandedWidth;
  }

  /**
   * Contracts the search text box.
   */
  contract() {
    const searchInput = document.querySelector(
      this.configuration.searchSelector,
    );

    searchInput.style.width = this.configuration.contractedWidth;
  }

  /**
   * Adds focus to the search text box.
   */
  focus() {
    const searchInput = document.querySelector(
      this.configuration.searchSelector,
    );

    searchInput.focus();
  }

  /**
   * Submits the search.
   * @param {*} e The activation event.
   */
  submit(e) {
    if (e.type === 'click' || e.key && e.key === 'Enter') {
      const searchInput = document.querySelector(
        this.configuration.searchSelector,
      );
      const searchText = searchInput.value;

      this.search(searchText);

      if (
        (this.configuration.behavior & this.behaviorType.Modal) ===
          this.behaviorType.Modal
      ) {
        const modalContainer = document.querySelector(
          this.configuration.modalClass,
        );

        modalContainer.style.display = 'none';
      }
    }
  }

  /**
   * Redirects to the search results page.
   * @param {string} searchText The search text to send.
   */
  search(searchText) {
    window.location = this.configuration.targetUrl + searchText;
  }

  /**
   * Adds the "expanded" class to the search text box.
   */
  toggleOn() {
    const searchInput = document.querySelector(
      this.configuration.searchSelector,
    );

    searchInput.classList.add(this.configuration.expandedClass);
    searchInput.classList.remove(this.configuration.contractedClass);
  }

  /**
   * Removes the "expanded" class from the search text box.
   */
  toggleOff() {
    const searchInput = document.querySelector(
      this.configuration.searchSelector,
    );

    searchInput.classList.remove(this.configuration.expandedClass);
    searchInput.classList.add(this.configuration.contractedClass);
  }

  /**
   * Shows the modal dialog box.
   */
  showModal() {
    const modalContainer = document.querySelector(
      this.configuration.modalClass,
    );
    const bodyEl = document.querySelector('body');
    modalContainer.style.display = 'block';

    bodyEl.classList.add(this.classNames.modalActive);
  }

  /**
   * Hides the modal dialog box.
   */
  hideModal() {
    const modalContainer = document.querySelector(
      this.configuration.modalClass,
    );
    const bodyEl = document.querySelector('body');

    modalContainer.style.display = 'none';
    bodyEl.classList.remove(this.classNames.modalActive);
  }
}
